if (!customElements.get("product-recommendations-multi")) {
  const ATHOS_WAIT_MS = 8000;
  const ATHOS_POLL_MS = 100;

  const slideKey = (slide) => {
    const link = slide.querySelector('a[href*="/products/"]');
    return link ? link.getAttribute("href").split("?")[0] : null;
  };

  const fetchDoc = (url, signal) =>
    fetch(url, { signal })
      .then((response) => (response.ok ? response.text() : ""))
      .then((text) =>
        text ? new DOMParser().parseFromString(text, "text/html") : null,
      )
      .catch(() => null);

  // Snap usually finishes its search before this section scrolls into view; afterStore covers the cold case.
  const athosController = (timeoutMs, signal) =>
    new Promise((resolve) => {
      if (signal.aborted) {
        resolve(null);
        return;
      }

      let deadline;
      let poll;
      let settled = false;

      const finish = (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(deadline);
        clearInterval(poll);
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      };
      const onAbort = () => finish(null);

      signal.addEventListener("abort", onAbort);
      deadline = setTimeout(() => finish(null), timeoutMs);
      poll = setInterval(() => {
        const controller =
          window.athos && window.athos.controller && window.athos.controller.search;
        if (!controller) return;
        clearInterval(poll);
        if (controller.store && controller.store.loaded) {
          finish(controller);
          return;
        }
        try {
          controller.on("afterStore", (data, next) => {
            finish(controller);
            return next();
          });
        } catch (e) {
          finish(null);
        }
      }, ATHOS_POLL_MS);
    });

  // result.id is the variant; the recommendations endpoint needs the product id, which sits on parentId.
  const productIds = (results, limit) => {
    const ids = [];
    for (const result of results || []) {
      const core = result && result.mappings && result.mappings.core;
      const attributes = result && result.attributes;
      const id = (core && core.parentId) || (attributes && attributes.groupId);
      if (!id) continue;
      const key = String(id);
      if (ids.includes(key)) continue;
      ids.push(key);
      if (ids.length >= limit) break;
    }
    return ids;
  };

  // Nothing matched the query, so the engine's own unfiltered ranking stands in as the seed.
  const popularProductIds = async (controller, limit, signal) => {
    let params;
    try {
      params = JSON.parse(JSON.stringify(controller.params || {}));
    } catch (e) {
      return [];
    }
    delete params.search;
    delete params.sorts;
    params.filters = (params.filters || []).filter((entry) => entry.background === true);
    params.pagination = { page: 1, pageSize: Math.max(limit, 1) };

    try {
      const response = await controller.client.search(params);
      if (signal.aborted) return [];
      return productIds(response && response.search && response.search.results, limit);
    } catch (e) {
      return [];
    }
  };

  customElements.define(
    "product-recommendations-multi",
    class ProductRecommendationsMulti extends HTMLElement {
      connectedCallback() {
        this.lifecycle = new AbortController();
        this.observer = new IntersectionObserver(
          (entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.unobserve(this);
            this.start();
          },
          { rootMargin: "0px 0px 400px 0px" },
        );

        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.lifecycle) this.lifecycle.abort();
      }

      async start() {
        const signal = this.lifecycle.signal;
        const athosPriority = this.dataset.athosPriority === "true";
        let loaded = false;

        if (athosPriority) {
          const urls = await this.athosSeedUrls(signal);
          if (signal.aborted) return;
          if (urls.length) loaded = await this.load(urls, signal);
        }
        if (!loaded) loaded = await this.load(null, signal);
        if (signal.aborted || loaded) return;

        this.clearLoader();
        // The Athos branch renders the shell without a Liquid seed, so an empty result set has to clear it.
        if (athosPriority) {
          const section = this.closest('[id^="shopify-section-"]');
          if (section) section.hidden = true;
        }
      }

      clearLoader() {
        const loader = this.querySelector("[data-recommendations-loader]");
        if (loader) loader.remove();
      }

      async athosSeedUrls(signal) {
        const controller = await athosController(ATHOS_WAIT_MS, signal);
        if (!controller || !controller.store || !controller.store.loaded) return [];

        const seedCount = parseInt(this.dataset.seedCount, 10) || 1;
        let ids = productIds(controller.store.results, seedCount);
        if (!ids.length && !controller.store.error) {
          ids = await popularProductIds(controller, seedCount, signal);
        }
        if (!ids.length) return [];

        const base = this.dataset.recommendationsUrl || "/recommendations/products";
        const sectionId = this.dataset.sectionId || "";
        const perSeed = parseInt(this.dataset.perSeed, 10) || 6;

        return ids.map(
          (id) =>
            `${base}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(id)}&limit=${perSeed}`,
        );
      }

      async load(seedUrls, signal) {
        let urls = seedUrls;
        if (!urls) {
          try {
            urls = JSON.parse(this.dataset.seedUrls || "[]");
          } catch (e) {
            urls = [];
          }
        }
        if (!urls.length) return false;

        const limit = parseInt(this.dataset.limit, 10) || 8;
        const docs = (await Promise.all(urls.map((url) => fetchDoc(url, signal)))).filter(Boolean);
        if (signal.aborted || !docs.length) return false;

        const buckets = docs.map((doc) =>
          Array.from(doc.querySelectorAll("[data-swiper-slide]")),
        );
        const merged = [];
        const seen = new Set();
        const depth = Math.max(...buckets.map((bucket) => bucket.length), 0);

        for (let i = 0; i < depth && merged.length < limit; i += 1) {
          for (const bucket of buckets) {
            if (merged.length >= limit) break;
            const slide = bucket[i];
            if (!slide) continue;
            const key = slideKey(slide);
            if (key) {
              if (seen.has(key)) continue;
              seen.add(key);
            }
            merged.push(slide);
          }
        }

        if (!merged.length) return false;

        const host = docs[0].querySelector(
          "product-recommendations, product-recommendations-multi",
        );
        const container = host && host.querySelector("[data-swiper-container]");
        if (!container) return false;

        container.innerHTML = merged.map((slide) => slide.outerHTML).join("");
        this.innerHTML = host.innerHTML;
        this.classList.add("product-recommendations--loaded");
        return true;
      }
    },
  );
}
